import * as THREE from "three";
import { BLOCKS } from "../world/blocks";
import { TILE_UV } from "../world/atlas";
import { FACES } from "./mesher";

const corners: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [0, 1],
  [1, 1],
  [1, 0],
];

/**
 * Builds a unit cube BufferGeometry (size 1, centered at origin) textured
 * according to the block's defined top, side, and bottom atlas tiles with
 * per-face directional shading.
 */
export function createBlockBufferGeometry(id: number, size = 1): THREE.BufferGeometry {
  const def = BLOCKS[id];
  if (!def || def.top < 0) {
    return new THREE.BoxGeometry(size, size, size);
  }

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  for (const face of FACES) {
    const tile = face.dir[1] > 0 ? def.top : face.dir[1] < 0 ? def.bottom : def.side;
    const uv = TILE_UV[tile];
    if (!uv) continue;

    const base = positions.length / 3;
    for (const [a, b] of corners) {
      positions.push(
        (face.base[0] + face.u[0] * a + face.v[0] * b - 0.5) * size,
        (face.base[1] + face.u[1] * a + face.v[1] * b - 0.5) * size,
        (face.base[2] + face.u[2] * a + face.v[2] * b - 0.5) * size,
      );
      normals.push(face.dir[0], face.dir[1], face.dir[2]);
      colors.push(face.shade, face.shade, face.shade);
    }

    uvs.push(uv.u0, uv.v0, uv.u1, uv.v0, uv.u1, uv.v1, uv.u0, uv.v1);
    indices.push(
      base + 1,
      base + 0,
      base + 3,
      base + 1,
      base + 3,
      base + 2,
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}
